type CacheEntry<T> = { savedAt: number; data: T };

const prefix = 'epos-bookings-reference';
const tokenKey = 'epos-bookings-token';
const staleTime = 5 * 60_000;

// Reference data is deliberately kept outside sessionStorage. The desktop
// connector can hide, navigate or replace its operational renderer while the
// same venue remains selected; sessionStorage made every such transition a
// cold start. Entries are tenant scoped, contain no bearer token, and are
// invalidated by the existing save mutations.
const storage = () => localStorage;

function scope() {
  const token = sessionStorage.getItem(tokenKey);
  if (!token) return 'anonymous';
  try {
    const encoded = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, '='))) as Record<string, string>;
    return payload.organisation_id || payload.sub || 'anonymous';
  } catch {
    return 'anonymous';
  }
}

function storageKey(name: string) {
  return `${prefix}:${scope()}:${name}`;
}

export const referenceCache = {
  get<T>(name: string): CacheEntry<T> | undefined {
    try {
      const raw = storage().getItem(storageKey(name));
      return raw ? JSON.parse(raw) as CacheEntry<T> : undefined;
    } catch {
      return undefined;
    }
  },
  set<T>(name: string, data: T) {
    try {
      storage().setItem(storageKey(name), JSON.stringify({ savedAt: Date.now(), data } satisfies CacheEntry<T>));
    } catch {
      // Storage can be unavailable or full. The network result remains usable.
    }
  },
  remove(name: string) {
    try { storage().removeItem(storageKey(name)); } catch { /* no-op */ }
  },
  removePrefix(namePrefix: string) {
    try {
      const scopedPrefix = storageKey(namePrefix);
      Object.keys(storage()).filter(key => key.startsWith(scopedPrefix)).forEach(key => storage().removeItem(key));
    } catch { /* no-op */ }
  },
  clearCurrentScope() {
    try {
      const scopedPrefix = `${prefix}:${scope()}:`;
      Object.keys(storage()).filter(key => key.startsWith(scopedPrefix)).forEach(key => storage().removeItem(key));
    } catch { /* no-op */ }
  }
};

export function referenceQueryOptions<T>(name: string, queryFn: () => Promise<T>, options: { staleTime?: number; gcTime?: number } = {}) {
  const cached = referenceCache.get<T>(name);
  return {
    queryKey: ['reference', name] as const,
    queryFn: async () => {
      const data = await queryFn();
      referenceCache.set(name, data);
      return data;
    },
    initialData: cached?.data,
    initialDataUpdatedAt: cached?.savedAt,
    staleTime: options.staleTime ?? staleTime,
    gcTime: options.gcTime ?? 30 * 60_000
  };
}
